
import { Vue, Component } from "vue-property-decorator";
import editor from "./editor"; // твой миксин
import moment from "moment-timezone";
import appConfigModule from "@/store/plugins/appConfig";

@Component({
  mixins: [editor],
})
export default class StringInlineEditor extends Vue {
  save: any;
  // Основное значение (хранится как строка в формате ISO: "2025-08-28T07:03:00")
  value: string | null = null;
  editMode: any;

  // Временное значение для редактирования (в формате DD.MM.YYYY HH:mm)
  tempDisplayValue: string = "";

  dataSource: any;
  field: any;

  // Календарь
  pickerMenu: boolean = false;
  pickerDate: string = "";
  pickerTime: string = "00:00";

  /**
   * Эти поля сервер разбирает по формату «дд.мм.гггг чч:мм» и ISO не принимает:
   * при любой правке даты события админка получала 400. Остальные поля дат
   * сохраняются в ISO, как и раньше.
   */
  static readonly DOTTED_DATE_FIELDS: Record<string, string[]> = {
    event: ["dateStart", "dateEnd", "registrationDateStart", "registrationDateEnd"],
    promocode: ["srtartDateTime", "endDateTime"],
  };

  /**
   * Даты события, урока и вебинара: сервер хранит их в мировом времени и пишет введённое как время
   * площадки (EventWallDateInput). Показывать их надо тоже во времени площадки, а не браузера —
   * иначе в Оше и в Москве одно событие выглядело по-разному, а ввод расходился с показом.
   * Пояс и порог перехода сервер отдаёт в /api/v1/config (timeZone, displayCutover).
   */
  static readonly SITE_TIME_FIELDS: Record<string, string[]> = {
    event: ["dateStart", "dateEnd", "registrationDateStart", "registrationDateEnd"],
    masterclass: ["dateStart", "dateEnd", "registrationDateStart", "registrationDateEnd"],
    webinar: ["dateStart", "dateEnd", "registrationDateStart", "registrationDateEnd"],
  };

  /** Начало для поля конца — чтобы не дать сохранить конец раньше начала. */
  static readonly END_TO_START: Record<string, string> = {
    dateEnd: "dateStart",
    registrationDateEnd: "registrationDateStart",
  };

  get siteTimeZone(): string | null {
    const className = String(this.dataSource?.className ?? "").toLowerCase();
    const fields = StringInlineEditor.SITE_TIME_FIELDS[className] ?? [];
    if (!fields.includes(this.field?.name)) return null;
    const tz = (appConfigModule as any).config?.timeZone;
    return tz && moment.tz.zone(tz) ? tz : null;
  }

  /**
   * Значение из API → время площадки. Дата с «Z» после порога перехода — мировая, переводим в пояс
   * площадки; до порога сервер хранит настенные цифры (дописывая «Z»), их показываем как есть —
   * зеркально TimestampWithoutTimeZoneConvention.FromStorage. Без «Z» (lorpro, «Дата проведения») —
   * цифры как есть.
   */
  toSiteMoment(raw: string | null | undefined): moment.Moment {
    if (!raw) return moment.invalid();
    const tz = this.siteTimeZone;
    const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(String(raw).trim());
    if (!tz || !hasZone) return moment(raw);
    const utc = moment.utc(raw);
    const cutover = (appConfigModule as any).config?.displayCutover;
    if (cutover && utc.format("YYYY-MM-DDTHH:mm:ss") < String(cutover)) {
      return moment(utc.format("YYYY-MM-DDTHH:mm:ss"));
    }
    return utc.clone().tz(tz);
  }

  get fieldLabel(): string {
    const title = this.field?.title ?? "";
    return this.siteTimeZone ? `${title} (время площадки, ${this.siteTimeZone})` : title;
  }

  get saveFormat(): string {
    const className = String(this.dataSource?.className ?? "").toLowerCase();
    const fields = StringInlineEditor.DOTTED_DATE_FIELDS[className] ?? [];
    return fields.includes(this.field?.name) ? "DD.MM.YYYY HH:mm" : "YYYY-MM-DDTHH:mm:ss";
  }

  // === Вычисляемые свойства ===

  public get displayValue(): string {
    if (!this.value) return "";
    // Нестрогий парс: API отдаёт дату в разных ISO-вариантах (с долями секунд/
    // смещением). Строгий moment(..., "YYYY-MM-DDTHH:mm:ss", true) их отвергал →
    // displayValue="" → пустой <p> без кликабельной области (нельзя было открыть
    // редактор у уже заполненной даты). moment(value) принимает валидный ISO.
    const m = this.toSiteMoment(this.value);
    if (!m.isValid()) return "";
    return m.format(this.dateFormat);
  }

  public get isValidValue(): boolean {
    if (!this.tempDisplayValue) return true;
    const m = moment(this.tempDisplayValue, this.dateFormat, true);
    return m.isValid();
  }

  public get placeholder(): string {
    return (this as any).field?.config?.dateFormat ?? "DD.MM.YYYY HH:mm";
  }

  public get dateFormat(): string {
    return "DD.MM.YYYY HH:mm";
  }

  public get maskValue(): string {
    return (this as any).field?.config?.mask ?? (this as any).field?.mask ?? "##.##.#### ##:##";
  }

  public get errorMessages(): string[] {
    if (!this.editMode) return [];
    if (!this.tempDisplayValue) return [];
    return this.isValidValue ? [] : ["Неверный формат даты"];
  }

  public get hideDetails(): boolean {
    return !!this.errorMessages.length;
  }

  public get notDefinedCaption(): string {
    return (this as any).field?.notDefinedCaption ?? "не задано";
  }

  public get readonly(): boolean {
    return (this as any).field?.readonly ?? false;
  }

  // === Методы ===

  setEditMode(): void {
    if (this.readonly) return;
    (this as any).editMode = true;
    this.tempDisplayValue = this.displayValue;
    this.$nextTick(() => {
      const input = this.$el.querySelector("input");
      if (input) input.focus();
    });
  }

  onInput(value: string): void {
    this.tempDisplayValue = value;
  }

  async saveDate(): Promise<void> {
    console.log("Сохраняем дату:", this.tempDisplayValue);

    // Если пусто — сохраняем null
    if (!this.tempDisplayValue || this.tempDisplayValue.trim() === "") {
      this.value = null;
      await this.save();
      return;
    }

    // Парсим ввод
    const m = moment(this.tempDisplayValue, this.dateFormat, true);
    if (m.isValid()) {
      // Конец не раньше начала: «конец = начало» и «конец через 3 часа» уже встречались в данных.
      const startName = StringInlineEditor.END_TO_START[this.field?.name];
      const startRaw = startName ? this.dataSource?.model?.[startName] : null;
      if (startRaw) {
        const start = this.toSiteMoment(startRaw);
        if (start.isValid() && !m.isAfter(moment(start.format("YYYY-MM-DDTHH:mm:ss")))
            && !window.confirm(`Конец (${m.format(this.dateFormat)}) не позже начала (${start.format(this.dateFormat)}). Сохранить всё равно?`)) {
          this.cancelChanges();
          return;
        }
      }
      // Формируем строку без UTC-сдвига: "2025-08-28T07:03:00" — это время площадки,
      // сервер сам переведёт его в мировое (EventWallDateInput).
      this.value = m.format(this.saveFormat);
      await this.save();
    } else {
      console.warn("Неверный формат даты, отмена изменений");
      this.cancelChanges();
    }
  }

  openPicker(): void {
    const current = this.toSiteMoment(this.value);
    const base = current.isValid() ? current : (this.siteTimeZone ? moment().tz(this.siteTimeZone) : moment());
    this.pickerDate = base.format("YYYY-MM-DD");
    this.pickerTime = base.format("HH:mm");
  }

  async applyPicker(): Promise<void> {
    if (!this.pickerDate) return;
    const picked = moment(`${this.pickerDate} ${this.pickerTime || "00:00"}`, "YYYY-MM-DD HH:mm", true);
    if (!picked.isValid()) return;
    this.pickerMenu = false;
    this.tempDisplayValue = picked.format(this.dateFormat);
    await this.saveDate();
  }

  cancelChanges(): void {
    (this as any).editMode = false;
    this.tempDisplayValue = "";
  }
}
